import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Upload, FileText, Image as ImageIcon, Trash2, Eye, Plus, ShieldAlert, Award,
} from "lucide-react";

const ACCEPT = "application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif,.pdf,.png,.jpg,.jpeg,.webp,.heic,.heif";

export default function Intyg() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ title: "", issuer: "", date: "" });
  const [upload, setUpload] = useState(null); // {attachment_id, storage_path, content_type, filename}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewingId, setViewingId] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/certificates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/certificates/upload", fd);
      setUpload(data);
      if (!form.title) setForm((f) => ({ ...f, title: (data.filename || "").replace(/\.[^.]+$/, "") }));
      toast.success("Fil uppladdad – fyll i uppgifterna och spara");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!upload) { toast.error("Ladda upp en fil först"); return; }
    if (!form.title.trim()) { toast.error("Ange en titel"); return; }
    setSaving(true);
    try {
      await api.post("/certificates", { data: { ...form, ...upload } });
      toast.success("Intyg sparat");
      setForm({ title: "", issuer: "", date: "" });
      setUpload(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort detta intyg?")) return;
    await api.delete(`/certificates/${id}`).catch(() => {});
    load();
  };

  const viewFile = async (id) => {
    setViewingId(id);
    try {
      const res = await api.get(`/certificates/${id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Kunde inte öppna filen"); }
    finally { setViewingId(null); }
  };

  return (
    <div>
      <PageHeader
        title="Betyg & intyg"
        description="Ladda upp betyg, certifikat, kursintyg och andra dokument som styrker din kompetens. De listas i ditt CV och i Mitt underlag och stärker din trovärdighet."
      />

      {/* Personnummer-varning */}
      <Card className="rounded-2xl border-amber-300 bg-amber-50 p-4 mb-6 flex items-start gap-3" data-testid="intyg-pnr-warning">
        <ShieldAlert className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900">
          <strong>Tänk på integriteten:</strong> maska gärna bort personnummer, betygssnitt du inte vill dela och andra känsliga uppgifter i dokumenten innan du laddar upp. Ett CV bör aldrig innehålla fullständigt personnummer.
        </p>
      </Card>

      {/* Ladda upp */}
      <Card className="rounded-2xl border-border p-5 md:p-6 mb-6" data-testid="intyg-upload-card">
        <div className="font-head font-semibold mb-4 flex items-center gap-2"><Plus className="h-5 w-5 text-primary" /> Lägg till intyg</div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-sm">Fil (PDF eller bild, max 15 MB)</Label>
            <div className="mt-1.5 flex items-center gap-3 flex-wrap">
              <input ref={fileRef} type="file" accept={ACCEPT} onChange={onFile} className="sr-only" id="cert-file" data-testid="intyg-file-input" />
              <label htmlFor="cert-file">
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 h-11 text-sm font-medium cursor-pointer hover:bg-muted min-h-[44px]" data-testid="intyg-file-btn">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Välj fil
                </span>
              </label>
              {upload && <span className="text-sm text-primary flex items-center gap-1.5"><FileText className="h-4 w-4" /> {upload.filename}</span>}
            </div>
          </div>
          <div>
            <Label className="text-sm">Titel</Label>
            <Input className="mt-1.5 h-11" placeholder="T.ex. Gymnasiebetyg, Truckkort A-B" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="intyg-title-input" />
          </div>
          <div>
            <Label className="text-sm">Utfärdare</Label>
            <Input className="mt-1.5 h-11" placeholder="T.ex. skola, myndighet, kursanordnare" value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} data-testid="intyg-issuer-input" />
          </div>
          <div>
            <Label className="text-sm">Datum</Label>
            <Input className="mt-1.5 h-11" placeholder="ÅÅÅÅ eller ÅÅÅÅ-MM" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="intyg-date-input" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={save} disabled={saving || !upload} className="rounded-full" data-testid="intyg-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />} Spara intyg
          </Button>
        </div>
      </Card>

      {/* Lista */}
      {items === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-border p-10 text-center" data-testid="intyg-empty">
          <Award className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Inga intyg uppladdade ännu. Lägg till ditt första ovan.</p>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="intyg-list">
          {items.map((it) => {
            const isPdf = (it.content_type || "").includes("pdf");
            return (
              <Card key={it.id} className="rounded-2xl border-border p-4 flex items-center justify-between gap-3" data-testid={`intyg-item-${it.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
                    {isPdf ? <FileText className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{it.title}</div>
                    <div className="text-sm text-muted-foreground truncate">{[it.issuer, it.date].filter(Boolean).join(" · ") || it.filename}</div>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-full min-h-[44px]" onClick={() => viewFile(it.id)} disabled={viewingId === it.id} data-testid={`intyg-view-${it.id}`}>
                    {viewingId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button size="sm" variant="ghost" className="rounded-full min-h-[44px] text-muted-foreground" onClick={() => remove(it.id)} data-testid={`intyg-delete-${it.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
